import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Container,
  Typography,
  List,
  Paper,
  CircularProgress,
  Alert,
  Divider,
  Box,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import "react-calendar-heatmap/dist/styles.css"; // Import default styles
// You might want to create a custom stylesheet for the heatmap to match your app's theme.
import useAuth from "../../hooks/useAuth";
import {
  useGetUserHabits,
  useCreateHabitEntry,
  useDeleteHabitEntry,
  useUpdateUserPreferences,
  getGetUserHabitsQueryKey,
  getGetProfileQueryKey,
} from "../../generated/hook";
import {
  HabitEntryOutboundDto,
  CreateHabitEntryInboundDto,
  HabitClass,
} from "../../generated/model";
import { useQueryClient } from "@tanstack/react-query";
import HabitCalendar from "../../components/HabitCalendar"; // Corrected import path for HabitCalendar
import { ViewPeriod } from "../../components/ContributionHeatmap/ContributionHeatmap.service";
import { HabitCountChart } from "../../components/HabitCountChart/HabitCountChart";
import StreakCelebration from "../../components/StreakCelebration";
import { useStreakCelebration } from "../../hooks/useStreakCelebration";

const HabitListPage: React.FC = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { celebration, checkAndCelebrate, closeCelebration } =
    useStreakCelebration();

  // Utiliser la préférence de l'utilisateur ou "week" par défaut
  const initialViewPeriod = (user?.preferredViewPeriod as ViewPeriod) || "week";
  const [viewPeriod, setViewPeriod] = useState<ViewPeriod>(initialViewPeriod);

  // Synchroniser la viewPeriod avec les préférences utilisateur quand elles changent
  useEffect(() => {
    if (user?.preferredViewPeriod) {
      const userPreference = user.preferredViewPeriod as ViewPeriod;
      setViewPeriod(userPreference);
    }
  }, [user?.preferredViewPeriod]);

  // Mutation pour mettre à jour les préférences utilisateur avec design optimiste
  const updatePreferencesMutation = useUpdateUserPreferences({
    mutation: {
      onSuccess: () => {
        // Invalider la requête de l'utilisateur connecté pour mettre à jour le cache
        queryClient.invalidateQueries({
          queryKey: getGetProfileQueryKey(),
        });
      },
      onError: (error, variables, context: any) => {
        console.error(t("habitList.errors.updatePreferences"), error);
        // En cas d'erreur, revenir à la valeur précédente stockée dans le contexte
        if (context?.previousViewPeriod) {
          setViewPeriod(context.previousViewPeriod);
        }
      },
      onMutate: async (variables) => {
        // Stocker la valeur actuelle pour le rollback potentiel
        const previousViewPeriod = viewPeriod;

        // Mettre à jour optimiste du cache de l'utilisateur connecté
        await queryClient.cancelQueries({
          queryKey: getGetProfileQueryKey(),
        });
        const previousUserData = queryClient.getQueryData(
          getGetProfileQueryKey()
        );

        // Mise à jour optimiste du cache
        queryClient.setQueryData(getGetProfileQueryKey(), (old: any) => {
          if (old) {
            return {
              ...old,
              preferredViewPeriod: variables.data.preferredViewPeriod,
            };
          }
          return old;
        });

        // Retourner le contexte pour le rollback
        return { previousViewPeriod, previousUserData };
      },
    },
  });

  const createHabitEntryMutation = useCreateHabitEntry({
    mutation: {
      onSuccess: () => {
        // Invalidate and refetch habits to update the heatmap
        queryClient.invalidateQueries({
          queryKey: getGetUserHabitsQueryKey(),
        });
      },
      onError: (error) => {
        // TODO: Handle error display to the user (e.g., toast notification)
        console.error(t("habitList.errors.createEntry"), error);
      },
    },
  });

  const deleteHabitEntryMutation = useDeleteHabitEntry({
    mutation: {
      onSuccess: () => {
        // Invalidate and refetch habits to update the heatmap
        queryClient.invalidateQueries({
          queryKey: getGetUserHabitsQueryKey(),
        });
      },
      onError: (error) => {
        // TODO: Handle error display to the user (e.g., toast notification)
        console.error(t("habitList.errors.deleteEntry"), error);
      },
    },
  });

  // Fetch habits using the generated hook
  // The actual hook name might differ, e.g., useListHabits, useGetHabitsByUser etc.
  // We'll use useGetUserHabits as a placeholder based on previous patterns.
  const {
    data: habitsResponse,
    isLoading: habitsLoading,
    error: habitsHttpError,
  } = useGetUserHabits();

  // Determine the actual list of habits and any potential inline error from the response
  let habitsResponseError: string | null = null;
  let habitEntries: HabitEntryOutboundDto[] = [];

  // Ensure habitsResponse is an array before using it
  const habits = Array.isArray(habitsResponse) ? habitsResponse : [];

  if (habits.length > 0) {
    // Extract all habit entries from all habits
    habitEntries = habits.flatMap((habit: any) => habit.habitEntries || []);
  }

  const handleDayClick = (date: string, habitId: string) => {
    // Find if an entry already exists for this habit and date
    const existingEntry = habitEntries.find(
      (entry) => entry.habitId === habitId && entry.date === date
    );

    if (existingEntry) {
      // If entry exists, delete it
      // Ensure existingEntry.id is the correct property name for the entry's ID
      deleteHabitEntryMutation.mutate({ id: existingEntry.id });
    } else {
      // Find the habit to get its current streak
      const habit = habits.find((h: HabitClass) => h.id === habitId);
      const previousStreak = habit?.currentStreak || 0;

      // If no entry exists, create a new one
      const newEntryDto: CreateHabitEntryInboundDto = {
        habitId,
        date, // Ensure this is in 'YYYY-MM-DD' format
        completed: true, // Clicking a day marks it as completed
        notes: "", // Optional: provide a way to add notes later if needed
      };

      createHabitEntryMutation.mutate(
        { data: newEntryDto },
        {
          onSuccess: () => {
            // After successful creation, check if we need to celebrate
            // Refetch the updated habit data
            queryClient.invalidateQueries({
              queryKey: getGetUserHabitsQueryKey(),
            });

            // Wait a bit for the data to refresh, then check for milestones
            setTimeout(() => {
              const updatedHabits = queryClient.getQueryData<HabitClass[]>(
                getGetUserHabitsQueryKey()
              );
              const updatedHabit = updatedHabits?.find(
                (h: HabitClass) => h.id === habitId
              );

              if (updatedHabit) {
                checkAndCelebrate(
                  previousStreak,
                  updatedHabit.currentStreak || 0,
                  (updatedHabit as any).name || "Habitude"
                );
              }
            }, 500);
          },
        }
      );
    }
  };

  const handleViewPeriodChange = (
    event: React.MouseEvent<HTMLElement>,
    newViewPeriod: ViewPeriod | null
  ) => {
    if (newViewPeriod !== null) {
      // Mise à jour optimiste de l'UI
      setViewPeriod(newViewPeriod);

      // Sauvegarder la préférence dans la base de données
      const preferencesData = {
        preferredViewPeriod: newViewPeriod,
      };

      updatePreferencesMutation.mutate({ data: preferencesData });
    }
  };

  if (authLoading || habitsLoading) {
    return (
      <Container sx={{ display: "flex", justifyContent: "center", mt: 5 }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!user) {
    navigate("/signin");
    return null; // or a loading/redirecting indicator
  }

  // Prioritize HTTP errors, then response errors
  const displayError =
    habitsHttpError ||
    (habitsResponseError ? new Error(habitsResponseError) : null);

  if (displayError) {
    return (
      <Container sx={{ mt: 5 }}>
        <Alert severity="error">
          {t("habitList.errors.loadHabits")}:{" "}
          {(displayError as Error)?.message || t("habitList.unknownError")}
        </Alert>
      </Container>
    );
  }

  return (
    <Container sx={{ mt: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        {t("habitList.title")}
      </Typography>

      {/* View Period Toggle */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {t("habitList.viewPeriod.label")}
        </Typography>
        <ToggleButtonGroup
          value={viewPeriod}
          exclusive
          onChange={handleViewPeriodChange}
          aria-label="view period"
          size="small"
        >
          <ToggleButton value="week" aria-label="week view">
            {t("habitList.viewPeriod.week")}
          </ToggleButton>
          <ToggleButton value="year" aria-label="year view">
            {t("habitList.viewPeriod.year")}
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {!!habits && habits.length > 0 ? (
        <Paper sx={{ mt: 2 }}>
          <List>
            {habits.map((habit: HabitClass, index: number) => (
              <React.Fragment key={habit.id}>
                <HabitCalendar
                  habit={habit}
                  habitEntries={habit.habitEntries || []}
                  onDayClick={handleDayClick}
                  viewPeriod={viewPeriod}
                />
                {index < habits.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Paper>
      ) : (
        <Typography sx={{ mt: 2 }}>
          {habitsResponseError ? "" : t("habitList.empty")}
        </Typography>
      )}

      {/* Add the HabitCountChart component below the habit list */}
      {!habitsResponseError && (
        <Box sx={{ mt: 4 }}>
          <HabitCountChart className="bg-white rounded-lg shadow" />
        </Box>
      )}

      {/* Streak Celebration Dialog */}
      {celebration.open && celebration.milestone && (
        <StreakCelebration
          open={celebration.open}
          onClose={closeCelebration}
          milestone={celebration.milestone}
          habitName={celebration.habitName}
        />
      )}
    </Container>
  );
};

export default HabitListPage;
